import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChatCircleDots, PaperPlaneTilt, Trash } from "@phosphor-icons/react";

// Facebook-style discussion board. Anyone can read; only signed-in users can post.
// Works for site boards and person boards via boardType + listUrl.
export function SiteDiscussion({ instId, boardId, boardType = "site", listUrl, t, placeholder }) {
  const { user } = useAuth();
  const id = boardId || instId;
  const url = listUrl || `/public/site/${id}/discussion`;
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(url).then((r) => setPosts(r.data || [])).catch(() => {});
  useEffect(() => { if (id) load(); /* eslint-disable-next-line */ }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/storyboards", { board_type: boardType, board_id: id, kind: "comment", text: text.trim() });
      setText("");
      await load();
    } catch {
      toast.error("Couldn't post. Please try again.");
    } finally { setBusy(false); }
  };

  const remove = async (pid) => {
    try { await api.delete(`/storyboards/${pid}`); setPosts((p) => p.filter((x) => x.id !== pid)); }
    catch { toast.error("Couldn't delete."); }
  };

  const when = (iso) => { try { return new Date(iso).toLocaleString("en", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div data-testid="site-discussion">
      {user ? (
        <form onSubmit={submit} className="mb-6 rounded-2xl border p-4" style={{ background: t.surface, borderColor: "#0001" }}>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="site-discussion-input"
            placeholder={placeholder || "Share a memory or start a conversation…"}
            className="w-full resize-none rounded-lg border bg-transparent p-3 text-sm outline-none" style={{ borderColor: "#0002", color: t.text }} />
          <div className="mt-2 flex justify-end">
            <button type="submit" disabled={busy || !text.trim()} data-testid="site-discussion-submit"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white disabled:opacity-50" style={{ background: t.primary }}>
              <PaperPlaneTilt size={15} /> {busy ? "Posting…" : "Post"}
            </button>
          </div>
        </form>
      ) : (
        <div className="mb-6 rounded-2xl border p-4 text-sm" style={{ background: t.surface, borderColor: "#0001" }}>
          <Link to="/login" className="font-medium" style={{ color: t.primary }}>Sign in</Link> to join the discussion and leave a comment.
        </div>
      )}

      {posts.length === 0 ? (
        <p className="flex items-center gap-2 opacity-60"><ChatCircleDots size={18} /> No comments yet — be the first to say hello.</p>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} className="flex gap-3 rounded-2xl border p-4" style={{ background: t.surface, borderColor: "#0001" }} data-testid={`site-discussion-post-${p.id}`}>
              {p.avatar_file_id ? <img src={fileUrl(p.avatar_file_id)} alt="" className="h-9 w-9 rounded-full object-cover" />
                : <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-white" style={{ background: t.primary }}>{(p.author_name || "?")[0]}</div>}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">{p.author_name}</span>
                  <span className="text-xs opacity-50">{when(p.created_at)}</span>
                </div>
                <p className="mt-1 whitespace-pre-line text-sm opacity-90">{p.text}</p>
              </div>
              {user && (user.id === p.author_id || user.role === "admin") && (
                <button onClick={() => remove(p.id)} className="opacity-40 transition-opacity hover:opacity-90" title="Delete" data-testid={`site-discussion-delete-${p.id}`}><Trash size={15} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SiteDiscussion;
